// The disposition control's state machine, kept apart from its rendering.
//
// Two reasons. A decision is the product's output, so the rule that a
// dismissal cannot commit without a reason should be a function that can be
// asserted directly rather than a disabled attribute that has to be inferred
// from markup. And the repository has no browser test runner — BRIEF.md rules
// out adding one — so the parts of this control a click drives are only
// testable if they are a reducer. See docs/redesign/ARCHITECTURE.md.

import type { Disposition, DismissReason } from '../../types';

/** Which secondary panel the control is showing, if any. */
export type DispositionPanel = 'none' | 'not-a-concern' | 'refer';

/**
 * What the reviewer has entered but not yet committed. Empty for an undecided
 * finding; seeded from the existing decision when they reopen one, so changing
 * a dismissal does not make them pick the reason again from nothing.
 */
export interface DispositionDraft {
  panel: DispositionPanel;
  /** True while a finding that already has a decision is being changed. */
  editing: boolean;
  reason: DismissReason | null;
  reasonText: string;
  note: string;
  /** "Ask an SME": a name or a role, both optional. */
  referredTo: string;
}

export type DispositionAction =
  | { type: 'open'; panel: DispositionPanel }
  | { type: 'cancel' }
  | { type: 'edit'; from: Disposition | null }
  | { type: 'reason'; reason: DismissReason }
  | { type: 'reason-text'; value: string }
  | { type: 'note'; value: string }
  | { type: 'referred-to'; value: string }
  | { type: 'committed' };

export function emptyDraft(): DispositionDraft {
  return {
    panel: 'none',
    editing: false,
    reason: null,
    reasonText: '',
    note: '',
    referredTo: '',
  };
}

/** The draft a reviewer sees when they reopen a decision they already made. */
export function draftFrom(disposition: Disposition | null): DispositionDraft {
  const base = emptyDraft();
  if (!disposition) return base;
  return {
    ...base,
    editing: true,
    panel:
      disposition.decision === 'not-a-concern'
        ? 'not-a-concern'
        : disposition.decision === 'refer'
          ? 'refer'
          : 'none',
    reason: disposition.reason ?? null,
    reasonText: disposition.reasonText ?? '',
    note: disposition.note ?? '',
    referredTo: disposition.referredTo ?? '',
  };
}

export function dispositionReducer(
  state: DispositionDraft,
  action: DispositionAction,
): DispositionDraft {
  switch (action.type) {
    case 'open':
      // Reopening the panel already showing closes it, so the control is
      // escapable without a decision.
      return {
        ...state,
        panel: state.panel === action.panel ? 'none' : action.panel,
      };
    case 'cancel':
      // Leaves the decision alone; only the uncommitted draft goes. Cancelling
      // a change puts the decision that is already on record back on screen,
      // so there is always a way out of the panel without deciding anything.
      return emptyDraft();
    case 'edit':
      return draftFrom(action.from);
    case 'reason':
      return { ...state, reason: action.reason };
    case 'reason-text':
      return { ...state, reasonText: action.value };
    case 'note':
      return { ...state, note: action.value };
    case 'referred-to':
      return { ...state, referredTo: action.value };
    case 'committed':
      return emptyDraft();
    default:
      return state;
  }
}

/**
 * Whether the open panel holds enough to commit.
 *
 * "Not a concern" needs a reason, and "Something else" needs the text that
 * explains it — a decision that records nothing about why is a decision that
 * cannot be defended three months later. "Ask an SME" needs nothing: the name
 * and the note are both offered, and both optional.
 */
export function canCommit(draft: DispositionDraft): boolean {
  if (draft.panel === 'not-a-concern') {
    if (draft.reason === null) return false;
    return draft.reason !== 'other' || draft.reasonText.trim().length > 0;
  }
  if (draft.panel === 'refer') return true;
  return false;
}

/** Empty strings are not stored; an absent field is how "not given" is held. */
function orUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * The record a commit writes. Returns null where the draft is not committable,
 * so a caller cannot write a dismissal with no reason by calling this directly.
 */
export function toDisposition(
  draft: DispositionDraft,
  decision: Disposition['decision'],
  findingId: string,
  now = new Date().toISOString(),
): Disposition | null {
  if (decision === 'include') {
    return {
      findingId,
      decision: 'include',
      note: orUndefined(draft.note),
      decidedAt: now,
    };
  }
  if (!canCommit(draft)) return null;
  if (decision === 'not-a-concern') {
    return {
      findingId,
      decision: 'not-a-concern',
      reason: draft.reason ?? undefined,
      reasonText: draft.reason === 'other' ? orUndefined(draft.reasonText) : undefined,
      note: orUndefined(draft.note),
      decidedAt: now,
    };
  }
  return {
    findingId,
    decision: 'refer',
    referredTo: orUndefined(draft.referredTo),
    note: orUndefined(draft.note),
    decidedAt: now,
  };
}
